import React, { FunctionComponent, ReactElement } from "react";
import {
  GameCodes,
  GameTitles,
  RoundHistoryComponentProps,
  RoundType,
} from "../../type";
import { MatchedLine, HippoSplashGameData, SpinHistory } from "./type";
import HippoSplashUtils from "./utils";
import "./hippo-splash.scss";
import { Link } from "react-router-dom";
import Utils from "../../utils";
import { GeneralRoundHistoryView } from "../../components";
import { useFocusSpinId } from "../../hooks";
import BigNumber from "bignumber.js";
import { useTranslator } from "../../context/TranslationContext";

const lineRatio = BigNumber(0.2);
export const HippoSplashRoundHistoryView: FunctionComponent<
  RoundHistoryComponentProps
> = ({ type, id, roundHistory }) => {
  const focusSpinId = useFocusSpinId(type, id);
  const translator = useTranslator();
  const spin =
    roundHistory.spins.find((s) => s.id === focusSpinId) ??
    roundHistory.spins[0];

  if (!spin) return <>Spin not found</>;

  if (!spin.focus) spin.focus = true;

  const plays = roundHistory.spins.reduce((ps, s) => {
    if (!ps[s.playRefNumber]) ps[s.playRefNumber] = [];
    ps[s.playRefNumber].push(s);
    return ps;
  }, {} as { [k: string]: SpinHistory[] });
  return (
    <GeneralRoundHistoryView
      gameCode={GameCodes.HippoSplash}
      roundHistory={roundHistory}
    >
      <div className="card mb-3">
        <div className="card-body">
          <div className="card-title fs-4 fw-bold">
            {translator.get("spin")}
            <button
              type="button"
              className="btn btn-info float-end"
              data-bs-toggle="modal"
              data-bs-target="#explaination"
            >
              <i className="bi bi-info-circle fw-bold"></i>
            </button>
          </div>

          <hr />
          {Object.values(plays).map((s) => (
            <PlayRecord
              key={s[0].playRefNumber}
              spins={s}
              betAmount={+roundHistory.betAmount}
              onSpinClicked={(spinId) => {
                spin.focus = false;
              }}
              buyFreeSpinRate={
                roundHistory.buyFreeSpin ? roundHistory.buyFreeSpinRate : 1
              }
              roundType={roundHistory.roundType}
            />
          ))}
        </div>
      </div>
      <div id="explaination" className={`modal fade`} tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{translator.get("explanation")}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="pb-3 fs-5">{translator.get("formula")}</div>
              <div className="pb-3">
                {translator.get("bet_size")} = {translator.get("bet")} x 0.2
              </div>
              <div className="pb-3">
                {translator.get("payout")} = {translator.get("bet_size")} x{" "}
                {translator.get("multiplier")}
              </div>
              <hr />
              <table className="w-100">
                <tbody>
                  <tr>
                    {[
                      [21, 250],
                      [201, 100],
                      [202, 25],
                      [203, 10],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {HippoSplashUtils.convertSymbol(+symbol)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    {[
                      [204, 8],
                      [205, 5],
                      [206, 3],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {HippoSplashUtils.convertSymbol(+symbol)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </GeneralRoundHistoryView>
  );
};
const PlayRecord: FunctionComponent<{
  spins: SpinHistory[];
  betAmount: number;
  onSpinClicked: (id: string) => void;
  buyFreeSpinRate: number;
  roundType: RoundType;
}> = ({
  spins,
  betAmount: rawBetAmount,
  onSpinClicked,
  buyFreeSpinRate: rawBuyFreeSpinRate,
  roundType,
}) => {
  const betAmount = BigNumber(rawBetAmount);
  const buyFreeSpinRate = BigNumber(rawBuyFreeSpinRate);
  const translator = useTranslator();
  const firstSpin = spins[0];
  const payout = spins.reduce((t, s) => t.plus(+s.winAmount), BigNumber(0));
  const elements = [
    <div
      key={`play-${firstSpin.playRefNumber}-id`}
      className="list-group-item list-group-item-primary text-center border fs-4"
    >
      {firstSpin.mode === "Base"
        ? translator.get("normal_spin_id")
        : translator.get("free_spin_id")}
      : <span className="fw-bold">{firstSpin.playRefNumber}</span>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-header`}
      className="list-group-item list-group-item-secondary spin-table"
    >
      <div className="d-flex text-center">
        <div className="col-4 border">{translator.get("spin_count")}</div>
        <div className="col-2 border">{translator.get("bet_size")}</div>
        <div className="col-3 border">{translator.get("payout")}</div>
        <div className="col-3 border">{translator.get("profit")}</div>
      </div>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-content`}
      className="list-group-item text-center border border"
    >
      <div className="d-flex">
        <div className="col-4 border">{spins.length - 1}</div>
        <div className="col-2 border">
          {Utils.asCurrency(
            betAmount.multipliedBy(lineRatio).dividedBy(buyFreeSpinRate),
            2
          )}
        </div>
        <div className="col-3 border">{Utils.asCurrency(payout)}</div>
        <div className="col-3 border">
          {Utils.asCurrency(
            firstSpin.reelMatrix.length > 1 &&
              firstSpin.mode === "Base" &&
              roundType !== RoundType.FreePlay
              ? payout.minus(betAmount).dividedBy(buyFreeSpinRate)
              : payout,
            2
          )}
        </div>
      </div>
    </div>,
  ] as ReactElement[];
  if (spins.length > 1) {
    spins.slice(0, -1).forEach((s) => {
      elements.push(
        <SpinRecord key={s.id} spin={s} onSpinClicked={onSpinClicked} />
      );
    });
  } else {
    const s = spins[0];
    elements.push(
      <SpinRecord key={s.id} spin={s} onSpinClicked={onSpinClicked} />
    );
  }

  return <div className="mb-2">{elements}</div>;
};

export const SpinRecord: FunctionComponent<{
  spin: SpinHistory;
  onSpinClicked: (id: string) => void;
}> = ({ spin, onSpinClicked }) => {
  const gameData = (spin.gameData as HippoSplashGameData) ?? {};
  const animationGroups = gameData.animationGroups ?? ({} as MatchedLine[]);
  const translator = useTranslator();
  const header = (
    <>
      <colgroup>
        <col width="30%" />
        <col width="15%" />
        <col width="15%" />
        <col width="15%" />
        <col width="15%" />
      </colgroup>

      <thead>
        <tr className="text-center list-group-item-secondary">
          <td className="">{translator.get("symbol")}</td>
          <td className="">{translator.get("line")}</td>
          <td className="">{translator.get("rate")}</td>
          <td className="">{translator.get("bonus")}</td>
          <td className="">{translator.get("payout")}</td>
        </tr>
      </thead>
    </>
  );

  let payoutTableRows: ReactElement[] = [];

  const isFeatureLastSpin =
    !gameData.featureSymbol || spin.winAmount !== "0.00";

  if (!isFeatureLastSpin) {
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-trigger-symbol`}>
        <td colSpan={999}>
          <b>{translator.get("bonus")}</b>
        </td>
      </tr>
    );
  } else if (Object.keys(animationGroups).length === 0) {
    if (spin.reelMatrix.length === 0) {
      payoutTableRows.push(
        <tr key={`payout-${spin.id}-trigger-symbol`}>
          <td colSpan={999}>
            <b>{translator.get("end")}</b>
          </td>
        </tr>
      );
    } else {
      payoutTableRows.push(
        <tr key={`payout-${spin.id}-trigger-symbol`}>
          <td colSpan={999}>
            <b>{translator.get("end")}</b>
          </td>
        </tr>
      );
    }
  }
  if (Object.keys(animationGroups).length > 0 && isFeatureLastSpin) {
    payoutTableRows.push(
      ...Object.entries(animationGroups).map((p, i) => {
        const symbolId = HippoSplashUtils.takeSymbolsByPayline(
          spin.reelMatrix,
          p[0]
        );
        return (
          <tr key={`payout-${spin.id}-${i}`}>
            <td>
              <div className="symbol flex">
                {HippoSplashUtils.convertSymbol(symbolId)}
              </div>
            </td>
            <td>
              <div className="symbol flex">
                {HippoSplashUtils.convertPayline(p[0])}
              </div>
            </td>
            <td>x{HippoSplashUtils.symbolPayout(symbolId)}</td>
            <td>{gameData.multiplier > 1 ? "x" + gameData.multiplier : "-"}</td>
            <td>{Utils.asCurrency(p[1].win)}</td>
          </tr>
        );
      })
    );

    payoutTableRows.push(
      <tr key={`payout-${spin.id}-hr`}>
        <td className="px-4" colSpan={999}>
          <hr />
        </td>
      </tr>
    );
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-total`} className="fw-bold fs-6">
        <td>{translator.get("win")}:</td>
        <td colSpan={3}></td>
        <td>{Utils.asCurrency(+spin.winAmount, 2)}</td>
      </tr>
    );
  }

  let content = (
    <div className="d-flex flex-wrap text-center ">
      <div className="col-12 col-md-6  py-3">
        <Reel
          reelMatrix={spin.reelMatrix}
          points={isFeatureLastSpin ? gameData.animationGroups || {} : {}}
        />
      </div>
      <div
        className={`col-12 col-md-6 pb-3 ${
          spin.focus ? " list-group-item-success" : ""
        }`}
      >
        <table width={"100%"}>
          {isFeatureLastSpin ? header : ""}
          <tbody>{payoutTableRows}</tbody>
        </table>
      </div>
    </div>
  );
  if (!spin.reelMatrix || spin.reelMatrix.length === 0) {
    content = (
      <div className={`col-12 pb-3 text-center`}>
        <table width={"100%"}>
          <tbody>{payoutTableRows}</tbody>
        </table>
      </div>
    );
  }

  return (
    <Link
      to={{ hash: spin.id }}
      id={spin.id}
      type="button"
      onClick={() => onSpinClicked(spin.id)}
      key={`spin-${spin.id}-id`}
      className={`list-group-item list-group-item-action border${
        spin.focus ? " border-success" : ""
      }`}
    >
      {content}
    </Link>
  );
};

export const Reel: FunctionComponent<{
  reelMatrix: number[][];
  points: { [k: number]: MatchedLine };
}> = ({ reelMatrix, points }) => {
  const matchedSet = new Set(
    Object.values(points).flatMap((m) => m.points.map((p) => `${p.x}-${p.y}`))
  );

  const reelColumn = reelMatrix.reduce((col, rawCol, x) => {
    const colElements = rawCol.reduce((row, rawSymbol, y) => {
      const key = `${x}-${y}`;
      const matched = matchedSet.has(key);

      row.push(<BoardCell key={key} symbol={rawSymbol} matched={matched} />);
      return row;
    }, [] as ReactElement[]);
    col.push(
      <div key={"col-" + x} className="d-flex flex-column">
        {colElements}
      </div>
    );
    return col;
  }, [] as ReactElement[]);

  return <div className="reel d-flex justify-content-center">{reelColumn}</div>;
};

const BoardCell: FunctionComponent<{
  symbol: number;
  matched: boolean;
}> = ({ symbol, matched }) => {
  const classes = ["symbol", "flex", "border", "border-dark"];
  let cellContent = HippoSplashUtils.convertSymbol(symbol);

  if (matched) {
    classes.push("animation");
  }

  return <div className={classes.join(" ")}>{cellContent}</div>;
};
